import React, { FC, memo, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { UnitType } from 'entities/Contract';
import { Box, useGetBoxRentalPriceString, useGetBoxRentalPriceStringWithVat } from 'entities/Box';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { getGlobalSettings } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { UserAccountType } from 'entities/User';
import { WarehouseForBooking } from 'entities/Warehouse';
import { SizeCodeType } from 'entities/SizeCode';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface ModernSelectedBoxInfoCardProps {
  boxInfo: Box;
  warehouseInfo: WarehouseForBooking;
  accountType?: Nullable<UserAccountType>;
}

export const ModernSelectedBoxInfoCard: FC<ModernSelectedBoxInfoCardProps> = memo((props) => {
  const { boxInfo, warehouseInfo, accountType } = props;

  const { t } = useAppTranslation('booking');

  const globalSettings = useAppSelector(getGlobalSettings);

  const showPriceWithVat = globalSettings?.showPriceWithVat;

  /* const discountedPrice = useGetBoxDiscountedPriceString({
    warehouseInfo,
    boxInfo,
    showPriceWithVat,
    accountType,
    unitType: boxInfo?.unitType,
  }); */

  /* const firstMonthDiscount = useMemo((): WarehouseDiscount | undefined => {
    const actualDiscount = warehouseInfo?.warehouseDiscounts?.find((discount) => {
      const includesFirstMonth = discount.discounts.split(',').some((discount) => FIRST_MONTH_DISCOUNT_REGEX.test(discount.trim()));

      const hasUnitSizes = Boolean(discount.unitSizes?.length);

      if (hasUnitSizes && boxInfo) {
        const unitSizes = discount.unitSizes.split(',').map((size) => Number(size));

        const includesSize = unitSizes.includes(boxInfo.sizeCode.square);

        return includesFirstMonth && includesSize;
      }

      return includesFirstMonth && discount.unitType === boxInfo?.unitType;
    });

    return actualDiscount;
  }, [boxInfo, warehouseInfo?.warehouseDiscounts]); */

  /* const parsedDiscount = parseDiscountData(firstMonthDiscount); */
  const boxMonthRentalPriceWithVat = useGetBoxRentalPriceStringWithVat(boxInfo, InvoiceFrequencyType.MONTH, warehouseInfo, accountType);
  const boxMonthRentalPrice = useGetBoxRentalPriceString(boxInfo);
  const rentalPrice = showPriceWithVat ? boxMonthRentalPriceWithVat : boxMonthRentalPrice;

  const size = `${formatDecimal(roundNumber(boxInfo?.height))} м (${t('H')}) × ${formatDecimal(roundNumber(boxInfo?.width))} м (${t(
    'W',
  )}) × ${formatDecimal(roundNumber(boxInfo?.length))} м (${t('D')})`;
  const square = boxInfo ? `${formatDecimal(roundNumber(boxInfo?.width * boxInfo?.length))} м²` : undefined;
  const volume = boxInfo ? `${formatDecimal(roundNumber(boxInfo.height * boxInfo.width * boxInfo.length))} м³` : undefined;

  const bullets = useMemo(() => {
    const isCubicBox = boxInfo.sizeCode.sizeCodeType === SizeCodeType.CUBIC_BOX;

    const baseBullets: TableRow[] =
      boxInfo?.unitType === UnitType.BOX
        ? [
            { label: t(`${boxInfo?.unitType} number`), value: boxInfo?.name },
            { label: t(isCubicBox ? 'Volume' : 'Square'), value: isCubicBox ? volume : square },
            { label: t('Floor'), value: getLocalizedString(boxInfo?.floor?.name) },
            { label: t('Box size'), value: size },
            { label: t('Regular price'), value: rentalPrice },
          ]
        : [
            { label: t(`${boxInfo?.unitType} number`), value: boxInfo?.name },
            { label: t('Square'), value: square },
            { label: t('Floor'), value: getLocalizedString(boxInfo?.floor?.name) },
            { label: t('Regular price'), value: rentalPrice },
          ];

    /* if (discountedPrice) {
      baseBullets.push({
        label: t('Promotional price'),
        value: (
          <div className="relative">
            {discountedPrice}
            <Popup description={<div className="max-w-[310px]">{parsedDiscount?.description}</div>}>
              <InfoIcon width={17} height={17} className="[&>path]:stroke-primary absolute cursor-pointer right-[-18px] top-0" />
            </Popup>
          </div>
        ),
      });
    } */

    return baseBullets;
  }, [boxInfo, rentalPrice, size, square, t]);

  return (
    <div className="bg-white rounded-lg p-4">
      <div className="font-semibold text-xl mb-2 text-primary font-tektur">{t('Your Box unit')}</div>

      {boxInfo?.description && <div className="text-md text-primaryLight mb-2">{getLocalizedString(boxInfo.description)}</div>}

      <BulletsTable rows={bullets} theme="clear" />
    </div>
  );
});
